import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { EmptyList } from "src/components/EmptyList";
import Loading from "src/components/Loading";
import SortIcon from "src/components/ui-icons/table/SortIcon";
import { TableHeaderColumnProps } from "src/components/ui/table/Table";
import {
  NotifyMyDocumentsType,
  useSelectDocumentContext,
} from "src/pages/DashboardPage/acadia/notificationPage/SelectDocuments";
import { FlexCol, FlexRow } from "src/utils/flexLayouts";
import { twMerge } from "tailwind-merge";

const tableHeaderContent = {
  code: {
    title: "Код",
    sort: true,
    style: { width: "77%" },
  },
  publicationDate: {
    title: "Дата публикации",
    sort: true,
    style: { width: "20%" },
  },
};

const NotifyTableWithFilters = ({ documents, loading }: { documents: NotifyMyDocumentsType[]; loading: boolean }) => {
  const limit = 10;

  if (loading) return <Loading />;

  return <Table tableName="Table with filters" header={tableHeaderContent} body={documents} limit={limit} />;
};

export default NotifyTableWithFilters;

export type CodeType = { code: string; name: string };

export type TableBodyContentItem = {
  id: number;
  code: CodeType;
  publicationDate: string;
};

function Table({
  header,
  body,
  tableName,
  limit,
}: {
  header: typeof tableHeaderContent;
  body: TableBodyContentItem[];
  limit: number;
  tableName: string;
}) {
  const [sortedField, setSortedField] = useState<keyof typeof tableHeaderContent | null>("publicationDate");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc" | "default">("desc");

  const handleSort = (field: keyof typeof tableHeaderContent) => {
    if (sortedField === field) {
      setSortDirection((prevDirection) =>
        prevDirection === "asc" ? "desc" : prevDirection === "desc" ? "default" : "asc"
      );
    } else {
      setSortedField(field);
      setSortDirection("asc");
    }
  };

  const sortedTableBodyContent = useMemo(() => {
    if (!sortedField || sortDirection === "default") {
      return body;
    }

    const sortedData = [...body];

    sortedData.sort((a, b) => {
      if (sortedField === "code") {
        return sortDirection === "asc"
          ? a[sortedField].code.localeCompare(b[sortedField].code)
          : b[sortedField].code.localeCompare(a[sortedField].code);
      }
      if (sortedField === "publicationDate") {
        return sortDirection === "asc"
          ? moment(a[sortedField], "DD.MM.YYYY").diff(moment(b[sortedField], "DD.MM.YYYY"))
          : moment(b[sortedField], "DD.MM.YYYY").diff(moment(a[sortedField], "DD.MM.YYYY"));
      }

      return 0;
    });

    return sortedData;
  }, [sortedField, sortDirection, body]) as TableBodyContentItem[];

  return (
    <TableList
      head={header}
      body={sortedTableBodyContent}
      sort={handleSort}
      sortDirection={sortDirection}
      sortedField={sortedField}
    />
  );
}

function TableList({
  head,
  body,
  sort,
  sortDirection,
  sortedField,
}: {
  head: typeof tableHeaderContent;
  body: NotifyMyDocumentsType[];
  sort: (f: keyof typeof tableHeaderContent) => void;
  sortedField: keyof typeof tableHeaderContent | null;
  sortDirection: "asc" | "desc" | "default";
}) {
  const { items, addItems, addItem, removeItem, removeItems, count } = useSelectDocumentContext();

  const [checkedAll, setCheckedAll] = useState(false);

  function checkAll(state: boolean) {
    if (state) {
      addItems(body);
    } else {
      removeItems(body);
    }

    setCheckedAll(state);
  }

  function handleCheckItem(item: NotifyMyDocumentsType, isCheck: boolean) {
    if (isCheck) {
      addItem(item);
    } else {
      removeItem(item.id);
    }
  }

  const tableFields = useMemo(() => {
    return Object.keys(head) as (keyof typeof tableHeaderContent)[];
  }, [head]);

  useEffect(() => {
    const isEveryBody = body.every((item) => items.has(item.id));

    if (body.length > 0 && isEveryBody) {
      setCheckedAll(true);
    } else {
      setCheckedAll(false);
    }
  }, [body, items, items.size]);

  const sortedListBySelected = useMemo(
    () => body.sort((a, b) => (items.has(a.id) > items.has(b.id) ? -1 : items.has(a.id) < items.has(b.id) ? 1 : 0)),
    [body, items]
  );

  return (
    <div className="w-auto bg-white rounded-lg table-custom shadow-custom overflow-auto scrollbar scrollbar-h-2 scrollbar-w-2 scrollbar-thumb-[#D1D5DB] max-h-[calc(100vh-412px)]">
      <FlexRow className="w-auto min-w-[825px]">
        <FlexCol className="relative grow">
          <FlexRow id="sticky-header" className="top-0 z-10 px-6 pt-5 pb-5 bg-white border-b gap-x-4 sticky-custom">
            <FlexCol
              className="break-words cursor-pointer"
              onClick={() => checkAll(!checkedAll)}
              style={{ width: "3%" }}
            >
              <input type="checkbox" checked={checkedAll} onChange={(e) => checkAll(!checkedAll)} className="sr-only" />
              <div
                className={twMerge(
                  checkedAll ? "border-darkblue" : "border-[#DFE4EA]",
                  "flex items-center justify-center w-5 h-5 mr-2 border rounded"
                )}
              >
                <span className={twMerge(checkedAll ? "opacity-100" : "opacity-0")}>
                  <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                      fill={"#012169"}
                      stroke={"#012169"}
                      strokeWidth="0.4"
                    />
                  </svg>
                </span>
              </div>
            </FlexCol>
            {tableFields.map((fieldKey) => {
              return (
                <TableHeaderColumn
                  key={fieldKey}
                  tableHeaderContent={head[fieldKey]}
                  sortDirection={sortedField === fieldKey ? sortDirection : "default"}
                  onClick={() => sort(fieldKey)}
                />
              );
            })}
          </FlexRow>
          <FlexRow className="px-6 pb-5">
            <FlexCol className="divide-y grow">
              {Array.isArray(sortedListBySelected) && sortedListBySelected.length > 0 ? (
                sortedListBySelected.map((item) => {
                  return (
                    <TableItem
                      key={item.id}
                      item={item}
                      isChecked={items.has(item.id)}
                      isCheckedAll={checkedAll}
                      handleCheckItem={handleCheckItem}
                    />
                  );
                })
              ) : (
                <EmptyList title="Документы не найдены" />
              )}
            </FlexCol>
          </FlexRow>
        </FlexCol>
      </FlexRow>
    </div>
  );
}

function TableItem({
  item,
  disabled = false,
  isChecked,
  isCheckedAll,
  handleCheckItem,
}: {
  item: NotifyMyDocumentsType;
  disabled?: boolean;
  isChecked: boolean;
  isCheckedAll: boolean;
  handleCheckItem: (item: NotifyMyDocumentsType, isCheck: boolean) => void;
}) {
  const [checked, setChecked] = useState(isChecked);

  function handleChange(input: boolean) {
    setChecked(input);
    handleCheckItem(item, input);
  }

  useEffect(() => {
    setChecked(isChecked);
  }, [isChecked]);

  return (
    <FlexRow
      className="py-4 gap-x-4 hover:bg-[#F3F4F6] hover:bg-opacity-50 rounded-lg cursor-pointer -mx-2 px-2"
      key={item.id}
      onClick={() => handleChange(!checked)}
    >
      <FlexCol className="break-words" style={{ width: "3%" }}>
        <input
          id={`document-${item.id}`}
          name={`document-${item.id}`}
          type="checkbox"
          checked={checked}
          onChange={() => handleChange(!checked)}
          className="sr-only"
        />
        <div
          className={twMerge(
            checked ? "border-darkblue" : "border-[#DFE4EA]",
            "flex items-center justify-center w-5 h-5 mr-2 border rounded"
          )}
        >
          <span className={twMerge(checked ? "opacity-100" : "opacity-0")}>
            <svg width="11" height="8" viewBox="0 0 11 8" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10.0915 0.951972L10.0867 0.946075L10.0813 0.940568C9.90076 0.753564 9.61034 0.753146 9.42927 0.939309L4.16201 6.22962L1.58507 3.63469C1.40401 3.44841 1.11351 3.44879 0.932892 3.63584C0.755703 3.81933 0.755703 4.10875 0.932892 4.29224L0.932878 4.29225L0.934851 4.29424L3.58046 6.95832C3.73676 7.11955 3.94983 7.2 4.1473 7.2C4.36196 7.2 4.55963 7.11773 4.71406 6.9584L10.0468 1.60234C10.2436 1.4199 10.2421 1.1339 10.0915 0.951972ZM4.2327 6.30081L4.2317 6.2998C4.23206 6.30015 4.23237 6.30049 4.23269 6.30082L4.2327 6.30081Z"
                fill={twMerge(disabled ? "#9CA3AF" : "#012169")}
                stroke={twMerge(disabled ? "#9CA3AF" : "#012169")}
                strokeWidth="0.4"
              />
            </svg>
          </span>
        </div>
      </FlexCol>
      <FlexCol className="break-words" style={{ width: "77%" }}>
        <div>{item.code.code}</div>
        <div className="text-darkblue">{item.code.name}</div>
      </FlexCol>
      <FlexCol className="break-words text-[#637381]" style={{ width: "20%" }}>
        <span>{item.publicationDate}</span>
      </FlexCol>
    </FlexRow>
  );
}

function TableHeaderColumn({ tableHeaderContent, sortDirection, onClick }: TableHeaderColumnProps) {
  return (
    <FlexCol className="select-none" style={tableHeaderContent.style}>
      <FlexRow className="items-center gap-x-2">
        {tableHeaderContent.icon && <FlexCol>{tableHeaderContent.icon}</FlexCol>}
        <FlexCol className="font-medium whitespace-pre-line w-fit">{tableHeaderContent.title}</FlexCol>
        {tableHeaderContent.sort && (
          <FlexCol className="w-auto cursor-pointer" onClick={onClick}>
            <SortIcon sortDirection={sortDirection} />
          </FlexCol>
        )}
      </FlexRow>
    </FlexCol>
  );
}
